(function () {
  if (window.customElements.get('bl-bundle-progress')) return;

  var STEP_CLASS = 'bl-bundle-progress__step';
  var STEP_COMPLETE = STEP_CLASS + '--complete';
  var STEP_CURRENT = STEP_CLASS + '--current';
  var STEP_PENDING = STEP_CLASS + '--pending';
  var STEP_GOAL = STEP_CLASS + '--goal';
  var STEP_ALMOST_THERE = STEP_CLASS + '--almost-there';
  var ARIA_DEBOUNCE_MS = 250;

  function getPhaseForCount(n, offerATotal) {
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < offerATotal) return 'a';
    // At/beyond offer_a_total: show A (complete) AND B simultaneously.
    // CSS hides Track B only when data-phase === "a".
    return 'both';
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  // Converts [[text]] → <span class="bl-msg-highlight">text</span>.
  // Mirrors the Liquid parser in snippets/bl-bundle-progress.liquid.
  // Idempotent for strings without [[ — fast early-out.
  // [[unclosed strings are left as-is (no regex match).
  function parseHighlight(text) {
    if (typeof text !== 'string' || text.indexOf('[[') === -1) return text;
    return text.replace(/\[\[([^\]]*?)\]\]/g, function (_, inner) {
      return '<span class="bl-msg-highlight">' + inner + '</span>';
    });
  }

  class BlBundleProgress extends HTMLElement {
    static get observedAttributes() {
      return ['item-count'];
    }

    constructor() {
      super();
      this._onCartChanged = this._onCartChanged.bind(this);
      this._ariaUpdateTimeout = null;
    }

    connectedCallback() {
      document.addEventListener('cart:change', this._onCartChanged);
      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChanged);
      if (this._ariaUpdateTimeout) {
        clearTimeout(this._ariaUpdateTimeout);
        this._ariaUpdateTimeout = null;
      }
    }

    attributeChangedCallback() {
      this._render();
    }

    get itemCount() {
      return parseInt(this.getAttribute('item-count'), 10) || 0;
    }

    set itemCount(value) {
      this.setAttribute('item-count', value);
    }

    get offerATotal() {
      return parseInt(this.dataset.offerATotal, 10) || 0;
    }

    get offerBTotal() {
      return parseInt(this.dataset.offerBTotal, 10) || 0;
    }

    _onCartChanged(event) {
      var cart = event && event.detail && event.detail.cart;
      if (!cart || typeof cart.item_count !== 'number') return;
      this.itemCount = cart.item_count;
    }

    _getMessageForCount(n) {
      if (n <= 0) return '';
      var raw = (n >= 6) ? (this.dataset.msg6Plus || '') : (this.dataset['msg' + n] || '');
      return parseHighlight(raw);
    }

    _render() {
      var itemCount = this.itemCount;
      var offerATotal = this.offerATotal;
      var offerBTotal = this.offerBTotal;
      var phase = getPhaseForCount(itemCount, offerATotal);

      this._updatePhase(phase);
      this._updateCheckpoints(itemCount, offerATotal, offerBTotal);
      this._updateAriaValues(itemCount, offerATotal);
      this._scheduleMessageUpdate(itemCount);
      this._updateTrackBSubtitle(itemCount);

      // Track B is shown (data-phase="both") from cart === offerATotal,
      // but stays desaturated until the customer engages with the bonus
      // by adding the first item beyond offerATotal (count >= +1).
      if (itemCount >= offerATotal + 1) {
        this.setAttribute('data-track-b-engaged', 'true');
      } else {
        this.removeAttribute('data-track-b-engaged');
      }

    }

    _updatePhase(phase) {
      if (this.getAttribute('data-phase') !== phase) {
        this.setAttribute('data-phase', phase);
      }
    }

    _updateCheckpoints(itemCount, offerATotal, offerBTotal) {
      var trackA = this.querySelector('[data-track-phase="a"]');
      if (trackA) {
        // 4th threshold is offerATotal (default 4) so the goal step
        // can actually reach COMPLETE at cart >= offerATotal.
        // The goal-specific almost-there branch in _updateTrack still
        // wins at cart === offerATotal - 1 (matches before the
        // generic `current` check).
        this._updateTrack(trackA, itemCount, [1, 2, 3, offerATotal]);
      }

      var trackB = this.querySelector('[data-track-phase="b"]');
      if (trackB) {
        // Track B has 2 dots after the original flag dot was removed:
        // new dot 1 = ex-position 2 (truck, threshold = offerATotal + 1)
        // new dot 2 = ex-position 3 (gift/trophy, threshold = offerBTotal)
        this._updateTrack(trackB, itemCount, [offerATotal + 1, offerBTotal]);
      }
    }

    _updateTrack(track, itemCount, thresholds) {
      var steps = track.querySelectorAll('.' + STEP_CLASS);
      // Goal step ALMOST_THERE: at cart === offerATotal - 1, the goal
      // pulses ("1 left"). This branch matches BEFORE the generic
      // `current` detection (itemCount === threshold - 1), so even
      // though threshold-1 also equals offerATotal-1 now, the
      // almost-there branch wins.
      var almostThereThreshold = this.offerATotal - 1;
      steps.forEach(function (step, index) {
        var threshold = thresholds[index];
        var isGoal = step.classList.contains(STEP_GOAL);

        step.classList.remove(STEP_COMPLETE, STEP_CURRENT, STEP_PENDING, STEP_ALMOST_THERE);

        if (itemCount >= threshold) {
          step.classList.add(STEP_COMPLETE);
        } else if (isGoal && itemCount === almostThereThreshold) {
          step.classList.add(STEP_PENDING);
          step.classList.add(STEP_ALMOST_THERE);
        } else if (itemCount === threshold - 1) {
          step.classList.add(STEP_CURRENT);
        } else {
          step.classList.add(STEP_PENDING);
        }
      });
    }

    _updateAriaValues(itemCount, offerATotal) {
      var trackA = this.querySelector('[data-track-phase="a"]');
      if (trackA) {
        trackA.setAttribute('aria-valuenow', String(clamp(itemCount, 0, 3)));
      }

      var trackB = this.querySelector('[data-track-phase="b"]');
      if (trackB) {
        // 2 dots now; valuenow range 0..2 (offerATotal → 0, offerBTotal → 2).
        trackB.setAttribute('aria-valuenow', String(clamp(itemCount - offerATotal, 0, 2)));
      }
    }

    _scheduleMessageUpdate(itemCount) {
      var self = this;
      if (this._ariaUpdateTimeout) {
        clearTimeout(this._ariaUpdateTimeout);
      }
      this._ariaUpdateTimeout = setTimeout(function () {
        // document.querySelector keeps the lookup robust regardless of
        // where the <p data-bl-bundle-progress-text> sits within the drawer.
        var target = document.querySelector('[data-bl-bundle-progress-text]');
        if (!target) return;
        var msgHTML = self._getMessageForCount(itemCount);
        if (itemCount >= 4) {
          target.innerHTML = '<span class="bl-bundle-progress__dynamic-msg-check" aria-hidden="true">✓</span>' + msgHTML;
          target.classList.add('bl-bundle-progress__dynamic-msg--unlocked');
        } else {
          target.innerHTML = msgHTML;
          target.classList.remove('bl-bundle-progress__dynamic-msg--unlocked');
        }
        self._ariaUpdateTimeout = null;
      }, ARIA_DEBOUNCE_MS);
    }

    _updateTrackBSubtitle(itemCount) {
      var bonusEl = document.querySelector('.bl-track-b-bonus');
      if (!bonusEl) return;
      var subtitleEl = bonusEl.querySelector('[data-track-b-subtitle]');

      // Quando Bonus Unlocked (count >= 6): remove subtitle se existir
      if (itemCount >= 6) {
        if (subtitleEl) subtitleEl.remove();
        return;
      }

      // Counts 4 e 5: cria/atualiza subtitle baseado nos data-attrs do host
      var newText = '';
      if (itemCount === 5) {
        newText = bonusEl.getAttribute('data-track-b-subtitle-c5') || '';
      } else if (itemCount === 4) {
        newText = bonusEl.getAttribute('data-track-b-subtitle-c4') || '';
      }

      if (newText && !subtitleEl) {
        var span = document.createElement('span');
        span.className = 'bl-track-b-bonus__subtitle';
        span.setAttribute('data-track-b-subtitle', '');
        span.innerHTML = parseHighlight(newText);
        bonusEl.appendChild(span);
      } else if (newText && subtitleEl) {
        subtitleEl.innerHTML = parseHighlight(newText);
      } else if (!newText && subtitleEl) {
        subtitleEl.remove();
      }
    }
  }

  window.customElements.define('bl-bundle-progress', BlBundleProgress);
})();
